import {
  DSDivider,
  DSText,
  DSTextEm,
  DSTextStrong,
  type DSBreakpointType,
  type DSIconName,
  type DSIconSize,
  type DSTextAlignType,
  type DSTextColorType,
  type DSTextFontFamilyType,
  type DSTextVariantType,
} from '@zoe/ds-web'
import type { ReactNode } from 'react'

import type { TypographyRecord } from '@/types/datocms-schema'
import type { FragmentComponent } from '@/types/graphql'

import { TypographyIcon } from '@/blocks/DesignSystem/TypographyIcon'

import { DSLinkText } from '@/components/ds/LinkText/LinkText'

const fragment = `
  fragment TypographyBlockFragment on TypographyRecord {
    __typename
    id
    content
    colour
    htmlTag
    elementId
    variant
    mobileVariant
    weight
    alignment
    mobileAlignment
  }
`

const translateOldWeightToNewWeight = (weight: string | undefined): DSTextFontFamilyType | undefined => {
  switch (weight) {
    case undefined:
      return undefined
    case 'lotaLight':
      return 'light'
    case 'lotaRegular':
      return 'regular'
    case 'lotaSemiBold':
      return 'semiBold'
    case 'nantes':
      return 'book'
    case 'nantesItalic':
      return 'bookItalic'
    default:
      return weight as DSTextFontFamilyType
  }
}

/**
 * The presentation options {@link processTypographyMarkdown} understands. Mirrors
 * the styling fields of a `TypographyRecord` — all optional, since most callers
 * render a bare string with a couple of overrides rather than a whole record.
 */
export type TypographyMarkdownProps = {
  content?: string | null
  // Required: the emphasis/link spans are rendered as `DSText*`, which needs one.
  variant: DSTextVariantType
  mobileVariant?: DSTextVariantType
  colour?: DSTextColorType
  weight?: DSTextFontFamilyType
  alignment?: DSTextAlignType
  mobileAlignment?: DSTextAlignType
  elementId?: string
  className?: string
}

/**
 * Replaces "\n" with line breaks in the input string
 */
const handleNewlines = (text: string): ReactNode[] => {
  return text
    .split('\n')
    .flatMap((line, index, array) => [line, ...(index < array.length - 1 ? [<br key={`br-${line}-${index}`} />] : [])])
}

/**
 * Returns the input string with the following sub-strings replaced by their respective components:
 * 1. "*text*" -> DSTextEm
 * 2. "**text**" -> DSTextStrong
 * 3. "[text](url)" -> DSLinkText
 * 4. "\n" -> <br />
 * 5. "{{icon:iconName:size}}" -> DSIcon name={iconName} size={size}
 */
export const processTypographyMarkdown = ({ content, ...props }: TypographyMarkdownProps): ReactNode[] => {
  const parts: ReactNode[] = []

  if (!content) return parts

  const markdownRegex = /(\*\*)([^*\n]+?)\1|(\*)([^*\n]+?)\3|\[(.*?)\]\((.*?)\)|{{icon:(.*?)(:([\d.]+))?}}|{{divider}}/g
  let lastIndex = 0

  content.replace(
    markdownRegex,
    // Capture groups, in order: the `**` marker, bold text, the `*` marker, italic
    // text, link text, link URL, icon name, the optional `:size` suffix, icon size.
    (
      match,
      _boldMarker: string | undefined,
      boldText: string | undefined,
      _italicMarker: string | undefined,
      italicText: string | undefined,
      linkText: string | undefined,
      linkUrl: string | undefined,
      iconName: string | undefined,
      _iconSizeSuffix: string | undefined,
      iconSize: string | undefined,
      offset: number,
      _source: string,
    ) => {
      // Add the plain text before the match
      if (offset > lastIndex) {
        const textBeforeMatch = content.slice(lastIndex, offset)
        parts.push(...handleNewlines(textBeforeMatch))
      }

      const commonProps = {
        variant: props.variant,
        color: props.colour,
        weight: translateOldWeightToNewWeight(props.weight),
        align: props.alignment,
        breakAt: {
          breakPoint: 'm' as DSBreakpointType,
          variant: props.mobileVariant || props.variant,
          align: props.mobileAlignment || props.alignment,
        },
        id: props.elementId,
        className: props.className,
      }

      if (boldText) {
        // Bold (**text**)
        parts.push(
          <DSTextStrong key={parts.length} as="span" {...commonProps} weight={undefined}>
            {boldText}
          </DSTextStrong>,
        )
      } else if (italicText) {
        // Italic (*text*)
        parts.push(
          <DSTextEm key={parts.length} as="span" {...commonProps} weight={undefined}>
            {italicText}
          </DSTextEm>,
        )
      } else if (linkText && linkUrl) {
        // Link ([text](url))
        parts.push(
          <DSLinkText
            colourScheme="navigation"
            href={linkUrl}
            key={parts.length}
            target="_blank"
            className={props.className}
          >
            {linkText}
          </DSLinkText>,
        )
      } else if (iconName) {
        // Name and size come from free-text CMS markdown (`{{icon:name:size}}`),
        // so they can only be checked against the DS unions at runtime.
        parts.push(
          <TypographyIcon
            key={parts.length}
            name={iconName as DSIconName}
            size={iconSize ? (Number(iconSize) as DSIconSize) : undefined}
          />,
        )
      } else if (match === '{{divider}}') {
        parts.push(
          <div style={{ display: 'inline-block', position: 'relative', top: '10px' }}>
            <DSDivider variant="primary" direction="vertical" height={32} spacing={24} />
          </div>,
        )
      }

      lastIndex = offset + match.length
      return match
    },
  )

  if (lastIndex < content.length) {
    const remainingText = content.slice(lastIndex)
    parts.push(...handleNewlines(remainingText))
  }

  return parts
}

export const TypographyBlock: FragmentComponent<{}, TypographyRecord> = ({ record }) => {
  return (
    <DSText
      variant={record?.variant}
      color={record?.colour}
      as={record?.htmlTag}
      weight={translateOldWeightToNewWeight(record?.weight)}
      align={record?.alignment}
      breakAt={{
        breakPoint: 'm',
        variant: record?.mobileVariant || record?.variant,
        align: record?.mobileAlignment || record?.alignment,
      }}
      id={record?.elementId}
    >
      {processTypographyMarkdown(record)}
    </DSText>
  )
}

TypographyBlock.fragment = fragment
TypographyBlock.recordName = 'TypographyRecord'
